'use client';

import { useEffect, useRef, useState, RefObject } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  baseOpacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
  baseSize: number;
}

interface ParticleBackgroundProps {
  containerRef: RefObject<HTMLElement>;
}

export default function ParticleBackground({ containerRef }: ParticleBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const particlesRef = useRef<Particle[]>([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number>();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      // Use the container's scrollHeight to cover full content
      const containerHeight = containerRef.current?.scrollHeight || window.innerHeight;
      canvas.height = containerHeight;
      
      // Calculate particle count based on screen area to maintain consistent density
      // Base density: 1 particle per 20,000 pixels (optimized for visual balance)
      const screenArea = canvas.width * canvas.height;
      const particleCount = Math.max(30, Math.min(250, Math.floor(screenArea / 20000)));
      
      // Reinitialize particles with new count
      const currentCount = particlesRef.current.length;
      if (currentCount !== particleCount) {
        particlesRef.current = Array.from({ length: particleCount }, () => {
          const baseOpacity = Math.random() * 0.4 + 0.4;
          const baseSize = Math.random() * 2 + 1;
          return {
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * 0.5,
            vy: (Math.random() - 0.5) * 0.5,
            size: baseSize,
            opacity: baseOpacity,
            baseOpacity: baseOpacity,
            twinkleSpeed: Math.random() * 0.02 + 0.01,
            twinklePhase: Math.random() * Math.PI * 2,
            baseSize: baseSize,
          };
        });
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Observe container size changes
    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY + window.scrollY };
    };

    window.addEventListener('mousemove', handleMouseMove);

    let frameCount = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;

      particlesRef.current.forEach((particle) => {
        // Mouse interaction
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          const force = (150 - distance) / 150;
          particle.vx -= (dx / distance) * force * 0.1;
          particle.vy -= (dy / distance) * force * 0.1;
        }

        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Boundary check
        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        // Damping
        particle.vx *= 0.99;
        particle.vy *= 0.99;

        // Automatic twinkling animation
        const twinkle = Math.sin(frameCount * particle.twinkleSpeed + particle.twinklePhase);
        particle.opacity = particle.baseOpacity + twinkle * 0.3;
        particle.size = particle.baseSize + Math.abs(twinkle) * 0.5;

        // Draw particle with glow effect
        const gradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size * 2
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${particle.opacity})`);
        gradient.addColorStop(0.4, `rgba(0, 184, 255, ${particle.opacity * 0.8})`);
        gradient.addColorStop(1, `rgba(0, 184, 255, 0)`);

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Core bright center
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        ctx.fill();

        // Draw connections
        particlesRef.current.forEach((otherParticle) => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = `rgba(0, 184, 255, ${0.15 * (1 - distance / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      resizeObserver.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHydrated, containerRef]);

  if (!isHydrated) {
    return (
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-secondary" />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 w-full"
      style={{ background: 'linear-gradient(135deg, #031b3a 0%, #0a2a4a 50%, #1a4b73 100%)' }}
    />
  );
}